/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */

import type { CallbackTimeslotDto } from './CallbackTimeslotDto';
import type { HealthcareProviderDto } from './HealthcareProviderDto';
import type { LanguageDto } from './LanguageDto';
import type { PatientDto } from './PatientDto';
import type { PatientReScreenerDto } from './PatientReScreenerDto';
import type { PatientScreenerDto } from './PatientScreenerDto';
import type { Protocol801ParticipationUpdateDto } from './Protocol801ParticipationUpdateDto';
import type { ReScreenerDto } from './ReScreenerDto';
import type { ScreenerDto } from './ScreenerDto';
import type { SiteInfoDto } from './SiteInfoDto';
import type { StudyInfoDto } from './StudyInfoDto';

export type ReferralDto = {
    id?: string;
    status: ReferralDto.status;
    study: StudyInfoDto;
    /**
     * Optimistic concurrency check
     */
    ocVersion?: number;
    consentText: string;
    note?: string;
    domain: string;
    language: LanguageDto;
    screener: ScreenerDto;
    patientScreener: PatientScreenerDto;
    offlineScreener: PatientScreenerDto;
    patient: PatientDto;
    referralDate: string;
    registrationDate: string;
    consentDateTime: string;
    appointmentDateTime?: string;
    postalCode?: string;
    locationBasedOnPostalCode?: string;
    distanceToPatientInKM?: number;
    ageAtTimeOfRegister?: number;
    gender?: ReferralDto.gender;
    /**
     * Optimistic concurrency check
     */
    sourceVersion?: number;
    /**
     * Device ID from Posthog
     */
    deviceId?: string;
    site?: SiteInfoDto;
    callbackTime: CallbackTimeslotDto;
    siteCallbackTime: CallbackTimeslotDto;
    lockDate: string;
    lockedBy: string;
    consentedDate: string;
    isVisited: boolean;
    reScreener: ReScreenerDto;
    patientReScreener: PatientReScreenerDto;
    availability?: string;
    protocol801Participation: Protocol801ParticipationUpdateDto;
    sponsorPatientID?: string;
    siterxStatus?: ReferralDto.siterxStatus;
    phoneNumber: string;
    isVisit601Completed?: string;
    siterxOptin?: boolean;
    seqsterOptin?: boolean;
    studySiteStatus?: ReferralDto.studySiteStatus;
    medicalRecordsRequestStatus?: ReferralDto.medicalRecordsRequestStatus;
    seqsterMrConsentReceived?: boolean;
    healthcareProvider?: HealthcareProviderDto | null;
}

export namespace ReferralDto {

    export enum status {
        NEW_REFERRAL = 'NEW_REFERRAL',
        ATTEMPTING_TO_CONTACT = 'ATTEMPTING_TO_CONTACT',
        CONTACTED = 'CONTACTED',
        CONTACTED__MEDICAL_RECORDS = 'CONTACTED__MEDICAL_RECORDS',
        CONTACTED__WASHOUT_PERIOD = 'CONTACTED__WASHOUT_PERIOD',
        CONTACTED__PATIENT_AVAILABILITY = 'CONTACTED__PATIENT_AVAILABILITY',
        CONTACTED__CALLBACK = 'CONTACTED__CALLBACK',
        APPOINTMENT_SCHEDULED = 'APPOINTMENT_SCHEDULED',
        WAITING_FOR_INFORMATION = 'WAITING_FOR_INFORMATION',
        WAITING_FOR_INFORMATION__MEDICAL_RECORD = 'WAITING_FOR_INFORMATION__MEDICAL_RECORD',
        WAITING_FOR_INFORMATION__WASHOUT_PERIOD = 'WAITING_FOR_INFORMATION__WASHOUT_PERIOD',
        WAITING_FOR_INFORMATION__WAITING_FOR_ICF = 'WAITING_FOR_INFORMATION__WAITING_FOR_ICF',
        CONSENTED = 'CONSENTED',
        RANDOMIZED = 'RANDOMIZED',
        SCREENING_FAILURE = 'SCREENING_FAILURE',
        DROPPED = 'DROPPED',
        DROPPED__UNABLE_TO_REACH = 'DROPPED__UNABLE_TO_REACH',
        DROPPED__FAILED_IN_EXCLUSION_CRITERIA = 'DROPPED__FAILED_IN_EXCLUSION_CRITERIA',
        DROPPED__NOT_INTERESTED = 'DROPPED__NOT_INTERESTED',
        DROPPED__BAD_OR_DUPLICATE_PROFILE = 'DROPPED__BAD_OR_DUPLICATE_PROFILE',
        DROPPED__TOO_FAR_AWAY = 'DROPPED__TOO_FAR_AWAY',
        DROPPED__OTHER = 'DROPPED__OTHER',
        DROPPED__NO_SHOW = 'DROPPED__NO_SHOW',
        IN_CALL_CENTER = 'IN_CALL_CENTER',
        IN_REVIEW = 'IN_REVIEW',
        ON_HOLD_IN_CALL_CENTER = 'ON_HOLD_IN_CALL_CENTER',
        DROPPED_BY_CC = 'DROPPED_BY_CC',
        DROPPED_BY_CC__UNABLE_TO_REACH = 'DROPPED_BY_CC__UNABLE_TO_REACH',
        DROPPED_BY_CC__FAILED_IN_EXCLUSION_CRITERIA = 'DROPPED_BY_CC__FAILED_IN_EXCLUSION_CRITERIA',
        DROPPED_BY_CC__NOT_INTERESTED = 'DROPPED_BY_CC__NOT_INTERESTED',
        DROPPED_BY_CC__BAD_OR_DUPLICATE_PROFILE = 'DROPPED_BY_CC__BAD_OR_DUPLICATE_PROFILE',
        DROPPED_BY_CC__TOO_FAR_AWAY = 'DROPPED_BY_CC__TOO_FAR_AWAY',
        DROPPED_BY_CC__DUE_TO_THE_END_OF_THE_ENROLLMENT = 'DROPPED_BY_CC__DUE_TO_THE_END_OF_THE_ENROLLMENT',
        DROPPED_BY_CC__OTHER = 'DROPPED_BY_CC__OTHER',
        RELOCATION = 'RELOCATION',
    }

    export enum gender {
        M = 'M',
        F = 'F',
        U = 'U',
    }

    export enum siterxStatus {
        PRE_CONSENT = 'Pre-Consent',
        NEW = 'New',
        MRR_SENT = 'MRR Sent',
        MRR_SIGNED = 'MRR Signed',
        DID_NOT_SIGN = 'Did Not Sign',
        PHYSICIAN_NOT_VALID = 'Physician Not Valid',
        PHYSICIAN_VALIDATED = 'Physician Validated',
        RECORDS_REQUESTED = 'Records Requested',
        PHYSICIAN_CONTACTED = 'Physician Contacted',
        RECORDS_NOT_RECEIVED = 'Records Not Received',
        RECORDS_RECEIVED = 'Records Received',
        READY_FOR_ELIGIBILITY_REVIEW = 'Ready for Eligibility Review',
        ELIGIBILITY_REVIEW = 'Eligibility Review',
        NOT_QUALIFIED = 'Not Qualified',
        DROPPED = 'Dropped',
        READY_FOR_STUDY = 'Ready for Study',
    }

    export enum studySiteStatus {
        SETUP = 'SETUP',
        CANCELED = 'CANCELED',
        ACTIVE = 'ACTIVE',
        INACTIVE = 'INACTIVE',
        PAUSED = 'PAUSED',
        CLOSEOUT = 'CLOSEOUT',
        COMPLETED = 'COMPLETED',
        REMOVED = 'REMOVED',
    }

    export enum medicalRecordsRequestStatus {
        PENDING = 'pending',
        AVAILABLE = 'available',
        NOT_FOUND = 'not_found',
        FAILED = 'failed',
    }


}
